<template>
  <v-card
    class="listing-score-preview"
    :style="score ? { border: `4px solid ${scoreProgressColor}` } : undefined"
    elevation="0"
    outlined
  >
    <div class="listing-score-preview__header">
      <div>
        <div class="listing-score-preview__eyebrow">SKOR LISTING</div>
        <h3>Estimasi kualitas listing</h3>
      </div>
      <v-progress-circular v-if="loading" indeterminate size="20" width="2" />
    </div>

    <template v-if="score">
      <div class="listing-score-preview__metrics">
        <div
          class="listing-score-preview__metric listing-score-preview__metric--primary"
          :class="`listing-score-preview__metric--${scoreTier}`"
          :style="metricStyle"
        >
          <span>Estimasi skor</span>
          <strong :style="metricNumberStyle">{{ estimatedScore }}</strong>
        </div>
        <div class="listing-score-preview__metric">
          <span>Skor maksimal</span>
          <strong>{{ maxScore }}</strong>
        </div>
      </div>

      <v-progress-linear
        :value="progress"
        :color="scoreProgressColor"
        background-color="#E7EFEC"
        rounded
        height="8"
      />

      <div
        v-if="showReminder"
        class="listing-score-preview__reminder"
        :class="`listing-score-preview__reminder--${scoreTier}`"
        role="note"
      >
        <v-icon small>mdi-lightbulb-outline</v-icon>
        <span
          >Lengkapi data listing agar skor listing dapat bertambah dan membantu meningkatkan
          performa listing.</span
        >
      </div>
    </template>

    <div v-else-if="error" class="listing-score-preview__empty">
      Score sementara belum dapat dihitung. Coba lagi setelah beberapa saat.
    </div>
    <div v-else class="listing-score-preview__empty">
      Lengkapi tipe properti untuk melihat estimasi score.
    </div>
  </v-card>
</template>

<script>
export default {
  name: 'listing-score-preview',
  props: {
    score: {
      type: Object,
      default: null,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    error: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    scoreData() {
      if (!this.score) return null;
      if (this.score.score) return this.score.score;
      if (this.score.data && this.score.data.score) return this.score.data.score;
      if (this.score.data) return this.score.data;
      return this.score;
    },
    estimatedScore() {
      return (
        this.scoreData?.estimated_score ?? this.scoreData?.total_score ?? this.scoreData?.score ?? 0
      );
    },
    maxScore() {
      return (
        this.scoreData?.max_score ??
        this.scoreData?.maximum_score ??
        this.scoreData?.profile?.total_max_score ??
        100
      );
    },
    progress() {
      if (!this.maxScore) return 0;

      return Math.min((Number(this.estimatedScore) / Number(this.maxScore)) * 100, 100);
    },
    scorePercentage() {
      const score = Number(this.estimatedScore || 0);
      const max = Number(this.maxScore || 100);
      return max > 0 ? (score / max) * 100 : score;
    },
    showReminder() {
      return this.scorePercentage < 80;
    },
    scoreTier() {
      const score = this.scorePercentage;

      if (score <= 40) return 'red';
      if (score <= 85) return 'yellow';
      return 'green';
    },
    scoreProgressColor() {
      if (this.scoreTier === 'red') return '#c81919';
      if (this.scoreTier === 'yellow') return '#f4bc04';
      return '#1e7a34';
    },
    metricStyle() {
      if (this.scoreTier === 'red') {
        return {
          backgroundColor: 'rgba(200, 25, 25, 0.08)',
        };
      }
      if (this.scoreTier === 'yellow') {
        return {
          backgroundColor: 'rgba(244, 188, 4, 0.15)',
        };
      }
      return {
        backgroundColor: 'rgba(30, 122, 52, 0.1)',
      };
    },
    metricNumberStyle() {
      if (this.scoreTier === 'red') {
        return {
          color: '#c81919',
        };
      }
      if (this.scoreTier === 'yellow') {
        return {
          color: '#b78103',
        };
      }
      return {
        color: '#1e7a34',
      };
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../../assets/scss/utils/variables';

.listing-score-preview {
  margin-top: 16px;
  padding: 20px 24px;
  border-radius: 16px !important;
  color: $color-text-black;
  transition: border-color 180ms ease;

  &__header,
  &__metrics {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  &__header {
    gap: 12px;
    margin-bottom: 18px;

    h3 {
      margin: 2px 0 0;
      font-family: Poppins-Bold;
      font-size: 16px;
      line-height: 1.35;
    }
  }

  &__eyebrow {
    color: $color-beliruma-1;
    font-family: Poppins-Bold;
    font-size: 10px;
    letter-spacing: 0.12em;
  }

  &__metrics {
    gap: 12px;
    margin-bottom: 16px;
  }

  &__metric {
    display: flex;
    min-width: 0;
    flex: 1;
    flex-direction: column;
    gap: 2px;
    padding: 12px;
    border-radius: 12px;
    background: $color-gray-2;

    span {
      color: $color-text-gray-2;
      font-size: 11px;
    }

    strong {
      font-family: Poppins-Bold;
      font-size: 24px;
      line-height: 1;
    }

    &--primary {
      background: rgba($color-success, 0.1);

      strong {
        color: $color-success;
      }
    }

    &--red {
      background: rgba($color-beliruma-1, 0.08) !important;

      strong {
        color: $color-beliruma-1 !important;
      }
    }

    &--yellow {
      background: rgba($color-warning, 0.15) !important;

      strong {
        color: #b78103 !important;
      }
    }

    &--green {
      background: rgba($color-success, 0.1) !important;

      strong {
        color: $color-success !important;
      }
    }
  }

  &__hint,
  &__empty {
    margin-top: 16px;
    color: $color-text-gray-2;
    font-size: 11px;
    line-height: 1.5;
  }

  &__reminder {
    display: flex;
    min-height: 44px;
    align-items: flex-start;
    gap: 8px;
    margin-top: 10px;
    padding: 11px 14px;
    border: 1px solid rgba($color-beliruma-1, 0.28);
    border-radius: 10px;
    background: rgba($color-beliruma-1, 0.04);
    color: $color-text-gray-2;
    font-size: 12px;
    line-height: 1.5;

    .v-icon {
      flex: 0 0 auto;
      margin-top: 1px;
      color: $color-beliruma-1;
      font-size: 18px;
    }

    &--red {
      border-color: rgba($color-beliruma-1, 0.42);
      background: rgba($color-beliruma-1, 0.05);

      .v-icon {
        color: $color-beliruma-1;
      }
    }

    &--yellow {
      border-color: rgba($color-warning, 0.55);
      background: rgba($color-warning, 0.08);

      .v-icon {
        color: #b78103;
      }
    }

    &--green {
      border-color: rgba($color-success, 0.4);
      background: rgba($color-success, 0.06);

      .v-icon {
        color: $color-success;
      }
    }
  }
}
</style>
